import type { AccountingProcessing, InterestClassBookSetting } from '@libs/enums';
import type { ItemNames, NumericRangeObject, OrEmpty } from '@ps-smart-cp/ppfw-client';
import {
  Characters,
  type ItemAttributes,
  ItemName,
  Locale,
  NumericRange,
  SingleDate,
  SingleValue,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  /**
   * 取得価額相当額(抽出条件)
   */
  acqsEquivalentValueCondition: NumericRangeObject;
  /**
   * 利息区分(抽出条件)
   */
  interestClassCondition: OrEmpty<InterestClassBookSetting>;
  /**
   * 会計処理(抽出条件)
   */
  accountingProcessCondition: OrEmpty<AccountingProcessing>;
  /**
   * 会計処理(グリッド用)
   */
  accountingProcessGrid: OrEmpty<AccountingProcessing>;
  /**
   * 取得価額相当額
   */
  acqsEquivalentValue: string;
  /**
   * 利息区分
   */
  interestClass: OrEmpty<InterestClassBookSetting>;
  /**
   * 1回当リース料
   */
  feesEachLeasing: string;
  /**
   * リース期首減損取崩残高
   */
  leasedAssetImpairmentReversalBegin: string;
  /**
   * リース期首減損累計額
   */
  accumulatedLeaseImpairmentBegin: string;
  /**
   * リース減損取崩残高
   */
  leasedAssetImpairmentReversal: string;
  /**
   * リース減損評価年月日
   */
  leaseImpairmentValuationDate: Date | null;
}

const attributes = {
  interestClass: SingleValue.shortValue(),
  interestClassCondition: SingleValue.shortValue(),
  accountingProcessCondition: SingleValue.shortValue(),
  acqsEquivalentValueCondition: NumericRange.of(0, 9_999_999_999_999, 0),
  accountingProcessGrid: Characters.preset.freeFormat(),
  acqsEquivalentValue: Characters.preset.freeFormat(),
  feesEachLeasing: Characters.preset.freeFormat(),
  leasedAssetImpairmentReversalBegin: Characters.preset.freeFormat(),
  accumulatedLeaseImpairmentBegin: Characters.preset.freeFormat(),
  leasedAssetImpairmentReversal: Characters.preset.freeFormat(),
  leaseImpairmentValuationDate: SingleDate.ymd(),
} satisfies ItemAttributes<Schema>;

interface Record {
  interestClass: string;
  acqsEquivalentValueFrom: string;
  acqsEquivalentValueTo: string;
  interestClassCondition: string;
  accountingProcessCondition: string;
  accountingProcess: string;
  acqsEquivalentValue: string;
  feesEachLeasing: string;
  leasedAssetImpairmentReversalBegin: string;
  accumulatedLeaseImpairmentBegin: string;
  leasedAssetImpairmentReversal: string;
  leaseImpairmentValuationDate: string;
}

const names = {
  interestClass: ItemName.of([Locale.ja, '利息区分']),
  interestClassCondition: ItemName.of([Locale.ja, '利息区分']),
  accountingProcessCondition: ItemName.of([Locale.ja, '会計処理']),
  acqsEquivalentValueCondition: ItemName.of([Locale.ja, '取得価額相当額']),
  accountingProcessGrid: ItemName.of([Locale.ja, '会計処理']),
  acqsEquivalentValue: ItemName.of([Locale.ja, '取得価額相当額']),
  feesEachLeasing: ItemName.of([Locale.ja, '1回当リース料']),
  leasedAssetImpairmentReversalBegin: ItemName.of([Locale.ja, 'リース期首減損取崩残高']),
  accumulatedLeaseImpairmentBegin: ItemName.of([Locale.ja, 'リース期首減損累計額']),
  leasedAssetImpairmentReversal: ItemName.of([Locale.ja, 'リース減損取崩残高']),
  leaseImpairmentValuationDate: ItemName.of([Locale.ja, 'リース減損評価年月日']),
} satisfies ItemNames<Schema>;

const titleNames = {
  leaseExpenseSummaryTable: ItemName.of([Locale.ja, 'リース費用集計表']),
};

const leaseAsset = {
  names,
  attributes,
  titleNames,
};

interface LeaseAsset {
  schema: Schema;
  record: Record;
}

export type { LeaseAsset };
export { leaseAsset };
